import {gql} from '@apollo/client';
import React from 'react';

import {assert} from 'Asserts';
import {SchemaCollaborator} from 'auth/PermissionConstants';
import {useSchemaCollaboratorsSection} from 'collaborator-components/useSchemaCollaboratorsSection';
import {writeFragmentsToCache} from 'graphql-client/ApolloWrappers';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import notifications from 'notifications';
import * as ProcessSchemaDAO from 'processes/dao/ProcessSchemaDAO';
import {SchemaWithOrgPermissionsSettings} from 'schemas/SchemaWithOrgPermissionsSettings';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';

import {
  ProcessTaskSchemaPermissionSettingsQuery,
  ProcessTaskSchemaPermissionSettingsQueryVariables,
} from './__generated/ProcessTaskSchemaPermissionSettingsQuery';

type MaybeInaccessibleProcessTaskSchema = ProcessTaskSchemaPermissionSettingsQuery['processTaskSchema'];

export type ProcessTaskSchemaWithPermissionSettings = ExtractByTypename<
  MaybeInaccessibleProcessTaskSchema,
  'ProcessTaskSchemaNode'
>;

export const PROCESS_TASK_SCHEMA_PERMISSION_SETTINGS_QUERY = gql`
  query ProcessTaskSchemaPermissionSettingsQuery($processTaskSchemaId: StrictString!)
  @skipAudit
  @engComponent(name: WORKFLOWS) {
    processTaskSchema(id: $processTaskSchemaId) {
      ... on PoliceableInterface {
        id
        permissions {
          definitionEditable
        }
        collaboratorsJson
      }
      ... on InaccessibleInfoNode {
        ...InaccessibleInfoGraphQL
      }
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

async function updateProcessTaskSchemaCollaborators(
  schemaId: string,
  data: {
    collaborators: Array<SchemaCollaborator>;
  }
): Promise<void> {
  const response = await ProcessSchemaDAO.updateCollaborators(schemaId, data);
  writeFragmentsToCache(getFragmentWrapper('CollaboratorInfo'), [response.schema]);
}

export interface QueryingProps {
  processTaskSchemaId: string;
}

function QueryingProcessTaskSchemaPermissionSettings({
  processTaskSchemaId,
}: QueryingProps): JSX.Element | null {
  const result = useBenchlingQuery<
    ProcessTaskSchemaPermissionSettingsQuery,
    ProcessTaskSchemaPermissionSettingsQueryVariables
  >(PROCESS_TASK_SCHEMA_PERMISSION_SETTINGS_QUERY, {
    variables: {processTaskSchemaId},
  });
  if (result.type === QueryResultType.ERROR) {
    notifications.error('Could not load access policies; try reloading the page.');
    return null;
  } else if (result.type === QueryResultType.PENDING) {
    return <CenteredHorizontalThrobber />;
  } else {
    assert(!isInaccessible(result.data.processTaskSchema), 'Expected process task schema to be accessible.');
    return (
      <ExistingProcessTaskSchemaPermissionSettings
        processTaskSchema={result.data.processTaskSchema}
        refetch={async () => {
          await result.refetch();
        }}
      />
    );
  }
}

export function ExistingProcessTaskSchemaPermissionSettings({
  processTaskSchema,
  refetch,
}: {
  processTaskSchema: ProcessTaskSchemaWithPermissionSettings;
  refetch: () => Promise<void>;
}): JSX.Element {
  const [{collaborators, minimalCollaborators}, handleUpdate] = useSchemaCollaboratorsSection(
    processTaskSchema.collaboratorsJson
  );
  return (
    <SchemaWithOrgPermissionsSettings
      id={processTaskSchema.id}
      isReadOnly={!processTaskSchema.permissions.definitionEditable}
      collaborators={collaborators}
      onUpdate={handleUpdate}
      onSubmit={async () => {
        await updateProcessTaskSchemaCollaborators(processTaskSchema.id, {
          collaborators: minimalCollaborators,
        });
        await refetch();
      }}
    />
  );
}

export default QueryingProcessTaskSchemaPermissionSettings;
